export type Phone = {
  phone: string
  active: boolean
  carrier?: { name?: string }
}

export type Mail = { mail: string; active: boolean }

export type UserLike = {
  voxuid: string
  firstName?: string
  lastName?: string
  language?: string
  plan?: string
  renew?: string | number
  transcriptions?: number
  extraTranscriptions?: number
  creationDate?: string | number
  phoneNumbers?: Phone[]
  mails?: Mail[]
  lastVoicemail?: {
    date?: string | number
    callerNumber?: string
    vmuid?: string
  }
}

export const formatDate = (value?: string | number) => {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const formatDateShort = (value?: string | number) => {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  })
}

export type RenewStatus =
  | { kind: 'expired'; days: number }
  | { kind: 'soon'; days: number }
  | { kind: 'ok'; days: number }
  | null

export const getRenewStatus = (value?: string | number): RenewStatus => {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000)
  if (days < 0) return { kind: 'expired', days }
  if (days <= 7) return { kind: 'soon', days }
  return { kind: 'ok', days }
}

export const buildUserSummary = (user: UserLike) => {
  const lines: string[] = []
  lines.push('USER')
  lines.push('----')
  lines.push(
    `Name      : ${[user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ') || '—'}`
  )
  lines.push(`Voxuid    : ${user.voxuid}`)
  lines.push(`Plan      : ${user.plan || '—'}`)
  lines.push(`Language  : ${user.language || '—'}`)
  lines.push(`Renew     : ${formatDateShort(user.renew)}`)
  const trans =
    user.transcriptions === undefined ? '—' : String(user.transcriptions)
  const extra =
    user.extraTranscriptions === undefined
      ? null
      : String(user.extraTranscriptions)
  lines.push(
    `Transcr.  : ${trans}${extra ? ` (+ ${extra} extra)` : ''}`
  )
  lines.push(`Created   : ${formatDateShort(user.creationDate)}`)

  const phones = user.phoneNumbers || []
  if (phones.length) {
    lines.push('')
    lines.push('PHONE NUMBERS')
    lines.push('-------------')
    phones.forEach((p) => {
      const status = p.active ? 'active' : 'inactive'
      const carrier = p.carrier?.name ? ` — ${p.carrier.name}` : ''
      lines.push(`- ${p.phone} (${status})${carrier}`)
    })
  }

  const mails = user.mails || []
  if (mails.length) {
    lines.push('')
    lines.push('EMAILS')
    lines.push('------')
    mails.forEach((m) => {
      lines.push(`- ${m.mail} (${m.active ? 'active' : 'inactive'})`)
    })
  }

  if (user.lastVoicemail) {
    lines.push('')
    lines.push('LAST VOICEMAIL')
    lines.push('--------------')
    lines.push(`Date    : ${formatDate(user.lastVoicemail.date)}`)
    lines.push(`Caller  : ${user.lastVoicemail.callerNumber || '—'}`)
    lines.push(`vmuid   : ${user.lastVoicemail.vmuid || '—'}`)
  }

  return lines.join('\n')
}

export const buildSupportEmailBody = (user: UserLike, notes: string) => {
  const lines: string[] = []
  lines.push('Hi,')
  lines.push('')
  lines.push(
    "I'm running into an issue with the user below. Could you take a look?"
  )
  lines.push('')
  lines.push('NOTES')
  lines.push('-----')
  lines.push(notes.trim() || '(no additional notes)')
  lines.push('')
  lines.push(buildUserSummary(user))
  lines.push('')
  lines.push('Thanks!')
  return lines.join('\n')
}
